import React from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  ButtonGroup,
  Button,
  Tooltip,
} from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import { Info } from "@mui/icons-material";
import { useTranslation } from "../../../../../../hooks/useTranslation";

type CompetitionTypeRadioGroupProps = {
  control: UseFormReturn["control"];
  translate: (_key: string) => string;
};

export const CompetitionTypeRadioGroup: React.FC<
  CompetitionTypeRadioGroupProps
> = ({ control, translate }) => {
  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      mb={2}
      gap={2}
    >
      <Typography
        variant="body2"
        fontWeight="bold"
        sx={{ width: { xs: "100%", sm: "20%" }, mb: { xs: 1, sm: 0 } }}
      >
        {translate("competitionType")}
      </Typography>
      <Controller
        name="competitionTarget"
        control={control}
        render={({ field: { onChange, value } }) => (
          <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
            <FormControlLabel
              value="LOWEST"
              control={<Radio />}
              label={translate("competeWithLowestPricedSeller")}
            />
            <FormControlLabel
              value="BUYBOX"
              control={<Radio />}
              label={translate("competeWithBuyBoxSeller")}
            />

            {value === "BUYBOX" && (
              <BuyboxMarginSelector translate={translate} control={control} />
            )}
          </RadioGroup>
        )}
      />
    </Box>
  );
};

const BuyboxMarginSelector = ({
  translate,
  control,
}: CompetitionTypeRadioGroupProps) => {
  const { translateWithoutPrefix } = useTranslation();
  return (
    <Controller
      name="noBuyBoxMargin"
      control={control}
      rules={{ required: true }}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Box gap={2} mt={2}>
          <Typography variant="body2" mb={1}>
            {translate("marginOptions.title")}:
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <ButtonGroup variant="outlined">
              {["MINIMUM", "STANDARD", "MAXIMUM"].map((marginOption) => (
                <Button
                  size="small"
                  key={marginOption}
                  variant={marginOption === value ? "contained" : "outlined"}
                  onClick={() =>
                    onChange(marginOption === value ? "" : marginOption)
                  }
                  sx={{
                    textTransform: "capitalize",
                    whiteSpace: "noWrap",
                  }}
                >
                  {translate("marginOptions." + marginOption)}
                </Button>
              ))}
            </ButtonGroup>
            <Tooltip title={translate("marginTooltip")}>
              <Info fontSize="small" color="primary" />
            </Tooltip>
          </Box>
          {error && (
            <Typography variant="caption" color="error">
              {translateWithoutPrefix("common.errors.required")}
            </Typography>
          )}
        </Box>
      )}
    />
  );
};
